import { parseDisplayJobId } from "@/helpers/bookingHelpers";
import { ClientBooking } from "@/redux/apis/clientBooking/types";
import { Booking } from "@/redux/slices/booking/types";

/**
 * Statuses that mean the driver has set off, and statuses that mean the trip itself has started —
 * the fallback half of the two flags that gate "Track Your Vehicle".
 *
 * They are a fallback because the provider payload does carry the real signals (see the flags
 * below); what it doesn't carry is anything for a job whose status someone advanced by hand rather
 * than through the driver app. Those jobs never get the timestamps at all — UAT J069302 reached
 * `closed` with `driver_on_way`, `client_pickup` and `client_dropped_off` all null — so a
 * signal-only gate would hide Track on exactly the in-progress jobs someone is watching, which is
 * what the ticket asked for. `on_way` and `picked_up` are the aliases older payloads send.
 */
const ON_WAY_STATUSES: string[] = ["on_way_to_job", "on_way"];
const ON_TRIP_STATUSES: string[] = ["commenced", "complete_awaiting_review", "picked_up"];

/**
 * Adapts an operator job (`bookingApi` / `/v3/provider/{op}/job`) to the client
 * `ClientBooking` shape so the existing BookingHistory columns + `BookingAction`
 * render verbatim for the operator's client-record Booking History.
 *
 * The provider job list carries only the display `jobNumber` (no numeric id), but that number is
 * `J` + the numeric job id, so `parseDisplayJobId` recovers the id `BookingAction` needs to open the
 * booking on the client detail route. The `is_editable`/`is_cancellable` flags aren't in this payload,
 * so they stay false — the booking sheet resolves editability from the fetched detail instead.
 */
export const toClientBooking = (job: Booking): ClientBooking => ({
  jobId: parseDisplayJobId(job.jobNumber),
  displayJobId: job.jobNumber,
  status: job.status,
  statusReason: "",
  pickupTime: job.dates.pickup,
  pickupTimezone: job.dates.timezone,
  commencementDate: job.dates.pickup,
  completionDate: null,
  // Operator job cost is in cents; ClientBooking.total is rendered as dollars.
  total: job.cost?.total != null ? job.cost.total / 100 : null,
  estimatedPayment: null,
  vehicleTotal: null,
  passengerName: job.passenger.name,
  passengerEmail: job.passenger.email,
  pickupLocation: job.stops?.[0]?.address ?? "",
  destination: job.stops && job.stops.length > 1 ? job.stops[job.stops.length - 1]?.address ?? "" : "",
  // Flight provenance for the pickup-time caption — the provider job hangs the flight off whichever
  // stop carries it (same lookup as the operator Bookings table), so the operator's view of a client's
  // history shows the caption identically.
  pickupDelayMinutes: job.stops?.find((stop) => stop.flight)?.flight?.pickupDelayMinutes ?? null,
  derivedArrival: job.stops?.find((stop) => stop.flight)?.flight?.pickupDerivedArrival ?? null,
  flags: {
    is_editable: false,
    // Operators/EAs can cancel a client's booking on-behalf (old-portal parity). The provider job list
    // carries the real cancellability as metadata.is_cancel_allowed (= Job::canCancel()), so gate the
    // Cancel action on it rather than hardcoding false; the backend re-checks on cancel.
    is_cancellable: job.metadata.isCancelAllowed,
    // Operators viewing a client's Booking History always see cost — pricing is only hidden from the
    // actual client. The provider job list has no per-row pricing flag, so default true here (the real
    // per-client state is resolved from the fetched booking detail when the sheet opens).
    is_pricing_available: true,
    // Gates the "Email Invoice" action for the operator's client-record Booking History — the old portal
    // showed it to operators viewing a client too. Sourced from the provider job list metadata
    // (JobEntityBase adds is_payment_invoice_generated).
    is_payment_invoice_generated: job.metadata.isPaymentInvoiceGenerated,
    is_ratable: false,
    is_special_event: false,
    is_asap: false,
    // Gates "Track Your Vehicle". Both were hardcoded false, which left the action unreachable on
    // every operator row while the client portal offered it on the same job.
    //
    // The first clause of each mirrors the client endpoint exactly, off the same underlying job
    // fields: its `is_driver_on_way` is `(bool) Job::getDriverOnWayDate()`, which the provider list
    // already exposes as `metadata.is_driver_on_way`; its `is_driver_on_trip` is
    // `(bool) Job::getDriverOnTrip()`, which the provider list exposes as `dates.client_pickup`
    // (JobEntityFullHydrator assigns `clientPickupDate` straight from `getDriverOnTrip()`). Neither
    // timestamp is ever cleared, so an on-trip job stays on-trip through review — as it does for the
    // client. The status clauses then go deliberately wider than the client; see above.
    is_driver_on_way: job.metadata.isDriverOnWay || ON_WAY_STATUSES.includes(job.status),
    is_driver_on_trip: !!job.dates.clientPickup || ON_TRIP_STATUSES.includes(job.status),
    is_passenger_dropped_off: false,
  },
});
